import { Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { REVIEW_MIN_COUNT } from "@/config/reviewsConfig";

interface RatingBadgeProps {
  ratingAvg: number | null | undefined;
  ratingCount: number | null | undefined;
  className?: string;
}

/**
 * Shared aggregate-rating badge (AC9). Text-equivalent for assistive tech — the
 * accessible name always carries the full meaning, never colour/glyph alone
 * (WCAG 1.4.1).
 *
 * - no reviews (count 0 / null / undefined) → renders nothing (no fabricated
 *   score, no DOM node). The null/undefined guard matters: an endpoint that omits
 *   the field (e.g. offering search before its rating aggregate shipped) would
 *   otherwise slip past a `=== 0` check and render "★ 0.0 ()".
 * - `0 < ratingCount < REVIEW_MIN_COUNT` → a "New" badge (never a leaked average).
 * - `ratingCount >= REVIEW_MIN_COUNT` → "★ {avg} ({count})".
 */
export function RatingBadge({ ratingAvg, ratingCount, className }: RatingBadgeProps) {
  if (!ratingCount || ratingCount <= 0) return null;

  if (ratingCount < REVIEW_MIN_COUNT) {
    // "New" — secondary tint (matches the mockup's .badge-new), distinct from the gold rating badge.
    return (
      <Badge
        data-testid="rating-badge"
        variant="outline"
        aria-label="New"
        className={cn(
          "gap-1 border-secondary/20 bg-secondary/5 text-secondary",
          className,
        )}
      >
        New
      </Badge>
    );
  }

  const avg = ratingAvg ?? 0;
  // Rating — star-gold/amber (matches the mockup's .badge-rating; MASTER.md "star gold" accent).
  return (
    <Badge
      data-testid="rating-badge"
      variant="outline"
      aria-label={`Rated ${avg.toFixed(1)} out of 5, ${ratingCount} reviews`}
      className={cn(
        "gap-1 border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50",
        className,
      )}
    >
      <Star className="w-3 h-3 fill-current" aria-hidden="true" />
      <span aria-hidden="true">
        {avg.toFixed(1)} ({ratingCount})
      </span>
    </Badge>
  );
}
